import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { customFetch, customFetchDownload } from "@workspace/api-client-react"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Semantic text check shared across master-data forms and mirrored on the API
// (see server `meaningfulText`): a meaningful value must contain at least one
// letter or number after trimming, so punctuation- or whitespace-only input
// ("---", "@#$", " . ") is rejected while names that include punctuation
// ("M&S Bank", "St. George's") are accepted.
const MEANINGFUL_TEXT_RE = /[\p{L}\p{N}]/u
export function isMeaningfulText(v: string): boolean {
  return MEANINGFUL_TEXT_RE.test(v.trim())
}

// Standardized max length for organizational legal names (mirrors the server
// `LEGAL_NAME_MAX_LEN`). Anchored to the GST e-Invoice / GSTR "Legal Name"
// (LglNm) statutory ceiling of 100 characters.
export const LEGAL_NAME_MAX_LEN = 100

// Positive monetary amount check — mirrors the server `validateRequiredAmount`
// so blank, non-positive, malformed and over-precision/over-large values are
// rejected client-side with identical messaging. `cleanValue` is the trimmed
// string safe to send to the API. ERP standard column is numeric(18,2) =>
// 16 integer digits + 2 decimals.
export function validateRequiredAmount(
  value: unknown,
  label = "Amount",
  opts: { scale?: number; integerDigits?: number } = {},
): { valid: boolean; error?: string; cleanValue?: string } {
  const scale = opts.scale ?? 2
  const integerDigits = opts.integerDigits ?? 16
  if (value === null || value === undefined) return { valid: false, error: `${label} is required.` }
  const str = String(value).trim()
  if (str === "") return { valid: false, error: `${label} is required.` }
  const num = Number(str)
  if (Number.isNaN(num)) return { valid: false, error: "Invalid monetary value." }
  if (num <= 0) return { valid: false, error: `${label} must be greater than zero.` }
  const moneyRe = new RegExp(`^\\d{1,${integerDigits}}(\\.\\d{1,${scale}})?$`)
  if (!moneyRe.test(str)) {
    const decimals = str.includes(".") ? str.split(".")[1] ?? "" : ""
    if (decimals.length > scale) return { valid: false, error: `${label} can have at most ${scale} decimal places.` }
    if (/^\d+(\.\d+)?$/.test(str)) return { valid: false, error: `${label} is too large.` }
    return { valid: false, error: "Invalid monetary value." }
  }
  return { valid: true, cleanValue: str }
}

// Non-negative monetary amount — mirrors server `validateNonNegativeAmount`.
// Optional fields may be blank; when present, zero is allowed and negatives
// are rejected with the same messaging as the API.
export function validateNonNegativeAmount(
  value: unknown,
  label = "Amount",
  opts: { scale?: number; integerDigits?: number; optional?: boolean } = {},
): { valid: boolean; error?: string; cleanValue?: string } {
  const scale = opts.scale ?? 2
  const integerDigits = opts.integerDigits ?? 16
  const optional = opts.optional ?? false

  if (value === null || value === undefined) {
    return optional ? { valid: true } : { valid: false, error: `${label} is required.` }
  }
  const str = String(value).trim()
  if (str === "") {
    return optional ? { valid: true } : { valid: false, error: `${label} is required.` }
  }

  const num = Number(str)
  if (Number.isNaN(num) || !Number.isFinite(num)) return { valid: false, error: "Invalid monetary value." }
  if (num < 0) return { valid: false, error: `${label} cannot be less than zero.` }

  const moneyRe = new RegExp(`^\\d{1,${integerDigits}}(\\.\\d{1,${scale}})?$`)
  if (!moneyRe.test(str)) {
    const decimals = str.includes(".") ? str.split(".")[1] ?? "" : ""
    if (decimals.length > scale) return { valid: false, error: `${label} can have at most ${scale} decimal places.` }
    if (/^\d+(\.\d+)?$/.test(str)) return { valid: false, error: `${label} is too large.` }
    return { valid: false, error: "Invalid monetary value." }
  }
  return { valid: true, cleanValue: str }
}

// Mirror of the backend `validateApprovedAmount`: reuses validateRequiredAmount
// for the positive-money rules, then enforces the CAPEX rule that the approved
// figure cannot exceed the requested amount. Keeps the approve dialog in sync
// with the API so invalid approvals are blocked before submission.
export function validateApprovedAmount(
  approved: unknown,
  requestedAmount: unknown,
): { valid: boolean; error?: string; cleanValue?: string } {
  const base = validateRequiredAmount(approved, "Approved Amount")
  if (!base.valid) return base
  const requestedNum = Number(requestedAmount)
  if (Number.isFinite(requestedNum) && requestedNum > 0 && Number(base.cleanValue) > requestedNum) {
    return { valid: false, error: `Approved Amount cannot exceed the Requested Amount (₹${requestedAmount}).` }
  }
  return base
}

// Mirror of the backend `validateEffectiveDateRange`: compares Effective From /
// Effective To as YYYY-MM-DD strings (lexical == chronological). Same-day is
// allowed and an open-ended range (no Effective To) is always valid. Keeps date
// pickers in sync with the API so invalid ranges are blocked before submission.
export function validateEffectiveDateRange(effectiveFrom: unknown, effectiveTo: unknown): { valid: boolean; error?: string } {
  const from = effectiveFrom == null ? "" : String(effectiveFrom).trim()
  const to = effectiveTo == null ? "" : String(effectiveTo).trim()
  if (from && to && to < from) {
    return { valid: false, error: "Effective To date cannot be earlier than Effective From date." }
  }
  return { valid: true }
}

// Validates that a bill / expense date is on or before the current date (YYYY-MM-DD).
// Reusable client-side helper to ensure reimbursement claims and expense documents
// cannot reference future dates.
export function validatePastOrPresentDate(
  dateValue: unknown,
  label = "Bill Date",
): { valid: boolean; error?: string } {
  if (dateValue == null) return { valid: true }
  const str = String(dateValue).trim()
  if (!str) return { valid: true }
  const _now = new Date()
  const todayStr = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, "0")}-${String(_now.getDate()).padStart(2, "0")}`
  if (str > todayStr) {
    return { valid: false, error: `${label} cannot be greater than the current date.` }
  }
  return { valid: true }
}


// IFSC: 4 letters + '0' + 6 alphanumerics (RBI standard, e.g. HDFC0001234).
const IFSC_RE = /^[A-Za-z]{4}0[A-Za-z0-9]{6}$/
export function isValidIfsc(v: string): boolean {
  return IFSC_RE.test(v.trim())
}

// Indian bank account number: 9–18 digits (mirrors the server `bankAccountNumber`).
const ACCOUNT_NUMBER_RE = /^\d{9,18}$/
export function isValidAccountNumber(v: string): boolean {
  return ACCOUNT_NUMBER_RE.test(v.trim())
}

// GSTIN: 15 chars — 2-digit state code + 10-char PAN + 1 entity code + 'Z' + 1
// checksum char (mirrors the server `isValidGstinFormat`). Case-insensitive.
// The first two chars must be a recognized GST state/UT code (01–38, 97, 99).
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
// GST State Master — centralized statutory reference for GST state/UT codes
// (mirrors the server `GST_STATES`). Single source of truth: GST_STATE_CODES is
// derived from it so codes and names never diverge, and the State Code dropdown
// is populated from the same list. Codes 25/28 are retained for backward
// compatibility; 97/99 are the statutory non-state entries.
export const GST_STATES: ReadonlyArray<{ code: string; name: string }> = [
  { code: "01", name: "Jammu and Kashmir" },
  { code: "02", name: "Himachal Pradesh" },
  { code: "03", name: "Punjab" },
  { code: "04", name: "Chandigarh" },
  { code: "05", name: "Uttarakhand" },
  { code: "06", name: "Haryana" },
  { code: "07", name: "Delhi" },
  { code: "08", name: "Rajasthan" },
  { code: "09", name: "Uttar Pradesh" },
  { code: "10", name: "Bihar" },
  { code: "11", name: "Sikkim" },
  { code: "12", name: "Arunachal Pradesh" },
  { code: "13", name: "Nagaland" },
  { code: "14", name: "Manipur" },
  { code: "15", name: "Mizoram" },
  { code: "16", name: "Tripura" },
  { code: "17", name: "Meghalaya" },
  { code: "18", name: "Assam" },
  { code: "19", name: "West Bengal" },
  { code: "20", name: "Jharkhand" },
  { code: "21", name: "Odisha" },
  { code: "22", name: "Chhattisgarh" },
  { code: "23", name: "Madhya Pradesh" },
  { code: "24", name: "Gujarat" },
  { code: "25", name: "Daman and Diu" },
  { code: "26", name: "Dadra and Nagar Haveli and Daman and Diu" },
  { code: "27", name: "Maharashtra" },
  { code: "28", name: "Andhra Pradesh (Old)" },
  { code: "29", name: "Karnataka" },
  { code: "30", name: "Goa" },
  { code: "31", name: "Lakshadweep" },
  { code: "32", name: "Kerala" },
  { code: "33", name: "Tamil Nadu" },
  { code: "34", name: "Puducherry" },
  { code: "35", name: "Andaman and Nicobar Islands" },
  { code: "36", name: "Telangana" },
  { code: "37", name: "Andhra Pradesh" },
  { code: "38", name: "Ladakh" },
  { code: "97", name: "Other Territory" },
  { code: "99", name: "Centre Jurisdiction" },
]
const GST_STATE_CODES = new Set<string>(GST_STATES.map((s) => s.code))
// GSTIN check digit (15th char) via the GSTN mod-36 algorithm over 0-9 then A-Z.
// Mirrors the server `gstinCheckDigit`.
const GSTIN_CODEPOINTS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ"
function gstinCheckDigit(first14: string): string {
  const mod = GSTIN_CODEPOINTS.length
  let factor = 2
  let sum = 0
  for (let i = first14.length - 1; i >= 0; i--) {
    const codePoint = GSTIN_CODEPOINTS.indexOf(first14.charAt(i))
    let addend = factor * codePoint
    factor = factor === 2 ? 1 : 2
    addend = Math.floor(addend / mod) + (addend % mod)
    sum += addend
  }
  return GSTIN_CODEPOINTS.charAt((mod - (sum % mod)) % mod)
}
export interface GstinValidationResult {
  isValid: boolean;
  error?: string;
}

export function validateGstinDetail(v: string | null | undefined): GstinValidationResult {
  if (!v || typeof v !== "string") {
    return { isValid: false, error: "GSTIN is required." };
  }
  const value = v.trim().toUpperCase();
  if (value.length === 0) {
    return { isValid: false, error: "GSTIN is required." };
  }
  if (value.length !== 15) {
    return { isValid: false, error: "GSTIN must contain exactly 15 characters." };
  }
  const stateCode = value.slice(0, 2);
  if (!GST_STATE_CODES.has(stateCode)) {
    return { isValid: false, error: "Invalid GST state code." };
  }
  if (!GSTIN_RE.test(value)) {
    return { isValid: false, error: "GSTIN format is invalid." };
  }
  return { isValid: true };
}

export function isValidGstin(v: string): boolean {
  return validateGstinDetail(v).isValid;
}

// Officially assigned GST state/UT code (01–38, 97, 99). Reuses the same set as
// the GSTIN validator so the standalone State Code field and the code embedded
// in a GSTIN stay consistent (mirrors the server `isValidGstStateCode`).
export function isValidGstStateCode(v: string): boolean {
  return GST_STATE_CODES.has(v.trim())
}

function triggerBrowserDownload(blob: Blob, filename: string): void {
  const href = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = href
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => {
    URL.revokeObjectURL(href)
  }, 1000)
}

/** Build the statutory FVU filename (mirrors server generateFvuFile). */
export function buildTdsFvuFilename(formType: string, quarter: string): string {
  const normalized = formType.toUpperCase()
  const m = /^(\d{4})Q([1-4])$/.exec(quarter)
  if (!m) return `Form-${normalized}-${quarter}.txt`
  const year = Number(m[1])
  const qNum = m[2]
  const fyStr = `${year}-${String(year + 1).slice(2)}`
  return `Form-${normalized}-${fyStr}-Q${qNum}.txt`
}

// Downloads a server-generated file (PDF, CSV, plain text, etc.) via an
// authenticated blob request and triggers a browser save dialog. Throws on
// non-2xx or empty bodies so callers can surface an error toast instead of
// saving an error payload.
//
// Prefer this over bare <a href="/api/..."> links: customFetchDownload attaches
// the Clerk Bearer token and X-Tenant-Id header. Plain navigation/iframe
// requests cannot, which causes auth/tenant failures for private documents.
export async function downloadPdf(path: string, filename: string): Promise<void> {
  const { blob, filename: serverFilename } = await customFetchDownload(path)
  triggerBrowserDownload(blob, serverFilename ?? filename)
}

/** Open a private API document in a new tab via authenticated blob fetch. */
export async function openAuthenticatedDocument(path: string): Promise<void> {
  const blob = await customFetch<Blob>(path, { responseType: "blob" })
  const href = URL.createObjectURL(blob)
  const opened = window.open(href, "_blank", "noopener,noreferrer")
  if (!opened) {
    // Popup blocked — fall back to same-tab navigation of the blob URL.
    window.location.assign(href)
  }
  setTimeout(() => {
    URL.revokeObjectURL(href)
  }, 60_000)
}

/**
 * PUT a file to either an S3/GCS presigned URL or the local authenticated
 * `/api/storage/uploads/direct` endpoint. Direct uploads must go through
 * customFetch so Bearer + X-Tenant-Id headers are attached.
 */
export async function putStorageUpload(uploadURL: string, file: File): Promise<void> {
  let pathnameWithQuery: string | null = null
  try {
    const parsed = new URL(uploadURL, typeof window !== "undefined" ? window.location.origin : "http://localhost")
    if (parsed.pathname.includes("/storage/uploads/direct")) {
      pathnameWithQuery = `${parsed.pathname}${parsed.search}`
    }
  } catch {
    // Relative or opaque URL — treat as direct only when the path matches.
    if (uploadURL.includes("/storage/uploads/direct")) {
      pathnameWithQuery = uploadURL.startsWith("/") ? uploadURL : null
    }
  }

  if (pathnameWithQuery) {
    await customFetch(pathnameWithQuery, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type || "application/octet-stream" },
      responseType: "json",
    })
    return
  }

  const putResp = await fetch(uploadURL, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type || "application/octet-stream" },
  })
  if (!putResp.ok) {
    throw new Error(`File upload failed (${putResp.status})`)
  }
}

// Standardized Employee Code Formatter
// Normalizes numeric-only employee codes or missing/legacy identifiers to the organization's
// standard format (e.g. ALEA-0245, ALEA-1021). Preserves already-formatted codes (e.g. ALEA-0042, EMP001).
export function formatEmployeeCode(code?: string | number | null, fallbackId?: number | null): string {
  if (code != null) {
    const str = String(code).trim();
    if (str) {
      if (/^[A-Za-z]+-/.test(str)) {
        return str;
      }
      if (/^\d+$/.test(str)) {
        return `ALEA-${str.padStart(4, "0")}`;
      }
      return str;
    }
  }
  if (fallbackId != null) {
    return `ALEA-${String(fallbackId).padStart(4, "0")}`;
  }
  return "ALEA-0000";
}

// Extracts human-readable error messages from API error objects/responses
export function getApiErrorMessage(error: unknown, defaultMsg = "An error occurred"): string {
  if (error && typeof error === "object") {
    const maybeError = error as {
      message?: string;
      data?: {
        error?: string;
        message?: string;
        details?: unknown;
        fields?: Record<string, string[]>;
      };
    };
    const fields = maybeError.data?.fields;
    if (fields) {
      const fieldMessage = Object.entries(fields)
        .flatMap(([field, messages]) =>
          messages.map((message) => (field === "_root" ? message : `${field}: ${message}`))
        )
        .join(" ");
      if (fieldMessage) return fieldMessage;
    }
    const details = maybeError.data?.details;
    const detailLines = Array.isArray(details)
      ? details.filter((line): line is string => typeof line === "string" && line.trim() !== "")
      : [];
    const base =
      maybeError.data?.error ??
      maybeError.data?.message ??
      maybeError.message;
    if (base && detailLines.length > 0) return `${base} ${detailLines.join(" ")}`;
    if (base) return base;
  }
  return defaultMsg;
}

/**
 * Centrally validates an Indian PAN (Permanent Account Number).
 * Returns { valid: true, cleanValue: string } or { valid: false, error: string }.
 */
export function validatePanValue(value: unknown): { valid: boolean; error?: string; cleanValue?: string } {
  if (value == null) {
    return { valid: false, error: "PAN is required." };
  }
  let str = String(value);

  // Auto-trim leading and trailing spaces
  str = str.trim();

  // Mandatory check
  if (str === "") {
    return { valid: false, error: "PAN is required." };
  }

  // Auto-uppercase
  str = str.toUpperCase();

  // Spaces check (reject embedded spaces)
  if (/\s/.test(str)) {
    return { valid: false, error: "Spaces are not allowed." };
  }

  // Special characters check
  if (/[^A-Z0-9]/.test(str)) {
    return { valid: false, error: "Special characters are not allowed." };
  }

  // Character length validation
  if (str.length !== 10) {
    return { valid: false, error: "PAN must contain exactly 10 characters." };
  }

  // Numeric and alphabetic position validation (AAAAA9999A)
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  if (!panRegex.test(str)) {
    return { valid: false, error: "Enter a valid PAN." };
  }

  return { valid: true, cleanValue: str };
}

// Payroll run period — mirrors server `payrollPeriodValidation.ts` (IST calendar).
export const PAYROLL_PERIOD_TIMEZONE = "Asia/Kolkata";
export const FUTURE_PAYROLL_RUN_ERROR = "Future payroll runs cannot be created.";

export function getCurrentPayrollPeriod(
  referenceDate: Date = new Date(),
  timeZone: string = PAYROLL_PERIOD_TIMEZONE,
): { year: number; month: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "numeric",
  }).formatToParts(referenceDate);
  const year = Number(parts.find((p) => p.type === "year")?.value);
  const month = Number(parts.find((p) => p.type === "month")?.value);
  return { year, month };
}

export function isFuturePayrollPeriod(
  month: number,
  year: number,
  referenceDate: Date = new Date(),
  timeZone: string = PAYROLL_PERIOD_TIMEZONE,
): boolean {
  const current = getCurrentPayrollPeriod(referenceDate, timeZone);
  return year > current.year || (year === current.year && month > current.month);
}

export function validatePayrollRunPeriod(
  month: unknown,
  year: unknown,
  referenceDate: Date = new Date(),
  timeZone: string = PAYROLL_PERIOD_TIMEZONE,
): { valid: boolean; error?: string } {
  const reqMonth = Number(month);
  const reqYear = Number(year);

  if (!Number.isInteger(reqMonth) || reqMonth < 1 || reqMonth > 12) {
    return { valid: false, error: "Invalid month or year" };
  }
  if (!Number.isInteger(reqYear) || reqYear < 2000) {
    return { valid: false, error: "Invalid month or year" };
  }

  if (isFuturePayrollPeriod(reqMonth, reqYear, referenceDate, timeZone)) {
    return { valid: false, error: FUTURE_PAYROLL_RUN_ERROR };
  }

  return { valid: true };
}

/** Year options for payroll run picker: current year and prior `yearsBack` years. */
export function buildPayrollYearOptions(
  referenceDate: Date = new Date(),
  yearsBack = 5,
  timeZone: string = PAYROLL_PERIOD_TIMEZONE,
): number[] {
  const { year: curY } = getCurrentPayrollPeriod(referenceDate, timeZone);
  return Array.from({ length: yearsBack + 1 }, (_, i) => curY - yearsBack + i).filter((y) => y >= 2000);
}


